import { useCallback, useEffect, useMemo, useState } from 'react'

import { TDuplicateHPRole } from 'clickNgo/containers/contactEditor/components/wrappedComponents/wrappedContactBasicInfo/models'
import { UNIQUE_ROLES } from 'clickNgo/entities/contact'
import { ContactFormContent } from 'clickNgo/entities/contact'
import { useHPRoleCheckDuplicates } from 'clickNgo/entities/contact'
import { LoadingState } from 'clickNgo/entities/contact'
import { getUpdateValue } from 'clickNgo/features/contact/contactCreator/components/steps/hpRolesStep/helpers/getUpdateValue'
import { useGetFormDataField } from 'cng-common/features/form'
import Logger from 'core/utils/Logger'

import { ContactEditorFormKeys } from '../../../../configs'
import {
  ContactEditorSettingFormFields,
  ContactFormMenuItems,
  TContactEditorData,
} from '../../../../models'

type Params = Pick<TContactEditorData, 'contact'>

export function useHPRoleChange({ contact }: Params) {
  const [duplicateHPRoles, setDuplicateHPRoles] = useState<TDuplicateHPRole[]>([])

  const [hpRoles] = useGetFormDataField<string[]>(
    ContactEditorFormKeys.hpRoles,
    ContactFormContent.fields.hpRoles.field
  )

  const [, setIsLoading] = useGetFormDataField<LoadingState>(
    ContactEditorFormKeys.settings,
    ContactEditorSettingFormFields.IsLoading
  )

  const [currentMenuItem] = useGetFormDataField<ContactFormMenuItems>(
    ContactEditorFormKeys.settings,
    ContactEditorSettingFormFields.CurrentMenuItem
  )

  const [editableMenuItems] = useGetFormDataField<ContactFormMenuItems[]>(
    ContactEditorFormKeys.settings,
    ContactEditorSettingFormFields.EditableMenuItems
  )

  const { checkHPRoleDuplicates } = useHPRoleCheckDuplicates({
    contact,
  })

  const hasDuplicateHPRoles = useMemo(() => Boolean(duplicateHPRoles?.length), [duplicateHPRoles])

  const onHPRolesChangeHandler = useCallback(
    async (newHPRoles: string[]) => {
      setIsLoading({ isLoading: true, message: 'Updating HP Roles...' })
      const { isAddAction, updatedValue } = getUpdateValue(hpRoles, newHPRoles)

      if (!updatedValue) {
        setIsLoading({ isLoading: false, message: '' })
        return
      }

      const isUniqueRole = UNIQUE_ROLES.includes(updatedValue)

      if (isUniqueRole) {
        if (isAddAction) {
          try {
            const contactWithDuplicates = await checkHPRoleDuplicates(updatedValue)

            if (contactWithDuplicates?.length) {
              setDuplicateHPRoles([
                ...duplicateHPRoles,
                { role: updatedValue, contacts: contactWithDuplicates },
              ])
            }
          } catch (error) {
            Logger.error(error)
          }
        } else {
          setDuplicateHPRoles(
            duplicateHPRoles.filter((duplicate) => duplicate.role !== updatedValue)
          )
        }
      }

      setIsLoading({ isLoading: false, message: '' })
    },
    [checkHPRoleDuplicates, duplicateHPRoles, hpRoles, setIsLoading]
  )

  useEffect(() => {
    if (editableMenuItems.includes(currentMenuItem)) {
      setDuplicateHPRoles([])
    }
  }, [currentMenuItem, editableMenuItems])

  return { duplicateHPRoles, hasDuplicateHPRoles, onHPRolesChangeHandler: onHPRolesChangeHandler }
}
